// ContactSubmissions.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../authentication_components/authContext';
import { ContactSubmissionsProvider, useContactSubmissions } from '../../authentication_components/ContactSubmissionsContext';
import {
    Search, Mail, Phone, MapPin, Calendar, Clock, User,
    CheckCircle, Circle, ChevronDown, MessageSquare, Filter,
    RefreshCw, FileText, X, AlertCircle, Loader2, Send,
    Trash2, AlertTriangle // Added Trash2 and AlertTriangle
} from 'lucide-react';

const ContactSubmissionsInner = () => {
    const { adminId } = useAuth();
    const {
        getSubmissions,
        getStats,
        markAsResolved,
        markAsUnresolved,
        deleteSubmission, // Added deleteSubmission from context
        searchSubmissions,
        formatSubmissionData,
        loading,
        error,
        clearError
    } = useContactSubmissions();

    // Local state
    const [submissions, setSubmissions] = useState([]);
    const [stats, setStats] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [filterStatus, setFilterStatus] = useState('unresolved');
    const [isLoadingInitial, setIsLoadingInitial] = useState(true);

    // Resolution Modal State
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [isResolving, setIsResolving] = useState(false);

    // Delete Modal State (NEW)
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [submissionToDelete, setSubmissionToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const itemsPerPage = 20;

    // Load submissions
    const loadSubmissions = useCallback(async (options = {}) => {
        try {
            const queryOptions = {
                limit: itemsPerPage,
                offset: (currentPage - 1) * itemsPerPage,
                ...options
            };

            if (filterStatus === 'resolved') {
                queryOptions.resolved = true;
            } else if (filterStatus === 'unresolved') {
                queryOptions.resolved = false;
            }

            const result = await getSubmissions(queryOptions);
            setSubmissions(result.submissions || []);
            setTotalCount(result.total_count || 0);
            setHasMore(result.has_more || false);
        } catch (err) {
            console.error('Error loading submissions:', err);
        }
    }, [getSubmissions, currentPage, filterStatus, itemsPerPage]);

    // Load stats
    const loadStats = useCallback(async () => {
        try {
            const result = await getStats();
            setStats(result.stats);
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    }, [getStats]);

    // Initial load
    useEffect(() => {
        const initializeData = async () => {
            setIsLoadingInitial(true);
            await Promise.all([loadSubmissions(), loadStats()]);
            setIsLoadingInitial(false);
        };
        initializeData();
    }, []);

    // Reload when filter or page changes
    useEffect(() => {
        if (!isLoadingInitial) {
            loadSubmissions();
        }
    }, [filterStatus, currentPage]);

    // Handle search
    const handleSearch = useCallback(async () => {
        if (!searchTerm.trim()) {
            loadSubmissions();
            return;
        }

        if (searchTerm.trim().length < 2) {
            return;
        }

        setIsSearching(true);
        try {
            const result = await searchSubmissions(searchTerm.trim(), {
                limit: itemsPerPage,
                ...(filterStatus === 'resolved' && { resolved: true }),
                ...(filterStatus === 'unresolved' && { resolved: false })
            });
            setSubmissions(result.submissions || []);
            setTotalCount(result.total_count || 0);
            setHasMore(result.has_more || false);
            setCurrentPage(1);
        } catch (err) {
            console.error('Error searching:', err);
        } finally {
            setIsSearching(false);
        }
    }, [searchTerm, searchSubmissions, filterStatus, loadSubmissions, itemsPerPage]);

    // Clear search
    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
        loadSubmissions();
    };

    // Handle resolve submission
    const handleResolve = async () => {
        if (!selectedSubmission) return;

        setIsResolving(true);
        try {
            await markAsResolved(selectedSubmission.submission_id, resolutionNotes, adminId);

            // Update local state
            setSubmissions(prev => prev.map(sub =>
                sub.submission_id === selectedSubmission.submission_id
                    ? { ...sub, resolved: true, resolution: resolutionNotes, admin_id: adminId }
                    : sub
            ));

            // Refresh stats
            await loadStats();

            // Close modal and reset
            setShowResolveModal(false);
            setSelectedSubmission(null);
            setResolutionNotes('');
        } catch (err) {
            console.error('Error resolving submission:', err);
        } finally {
            setIsResolving(false);
        }
    };

    // Handle unresolve submission
    const handleUnresolve = async (submission) => {
        try {
            await markAsUnresolved(submission.submission_id);

            // Update local state
            setSubmissions(prev => prev.map(sub =>
                sub.submission_id === submission.submission_id
                    ? { ...sub, resolved: false, resolution: null, admin_id: null }
                    : sub
            ));

            // Refresh stats
            await loadStats();
        } catch (err) {
            console.error('Error unresolving submission:', err);
        }
    };

    // --- NEW DELETE HANDLERS ---

    const openDeleteModal = (submission) => {
        setSubmissionToDelete(submission);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!submissionToDelete) return;

        setIsDeleting(true);
        try {
            await deleteSubmission(submissionToDelete.submission_id);

            // Update local state by removing the deleted item
            setSubmissions(prev => prev.filter(sub => sub.submission_id !== submissionToDelete.submission_id));

            // Decrease total count locally
            setTotalCount(prev => prev - 1);

            // Refresh stats to ensure numbers are accurate
            await loadStats();

            // Close modal
            setShowDeleteModal(false);
            setSubmissionToDelete(null);
        } catch (err) {
            console.error('Error deleting submission:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    // ---------------------------

    const openResolveModal = (submission) => {
        setSelectedSubmission(submission);
        setResolutionNotes('');
        setShowResolveModal(true);
    };

    const handleRefresh = async () => {
        setCurrentPage(1);
        setSearchTerm('');
        await Promise.all([loadSubmissions(), loadStats()]);
    };

    // Render submission card
    const renderSubmissionCard = (submission) => {
        const formatted = formatSubmissionData(submission);

        return (
            <div
                key={submission.submission_id}
                className="bg-white rounded-xl shadow-md border border-orange-200 p-5 hover:shadow-lg transition-all duration-200 flex flex-col h-full"
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${formatted.resolved ? 'bg-green-100' : 'bg-orange-100'}`}>
                            {formatted.resolved ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                                <Circle className="h-5 w-5 text-orange-500" />
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-800 flex items-center">
                                <User className="h-4 w-4 mr-2 text-gray-500" />
                                {submission.name}
                            </h3>
                            <p className="text-xs text-gray-500">{submission.submission_id}</p>
                        </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${formatted.resolved
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                        }`}>
                        {formatted.statusText}
                    </span>
                </div>

                {/* Contact Info */}
                <div className="space-y-2 mb-4 flex-grow">
                    <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2 text-orange-500" />
                        <a href={`mailto:${submission.email}`} className="hover:text-orange-600 truncate">
                            {submission.email}
                        </a>
                    </div>

                    {formatted.hasPhone && (
                        <div className="flex items-center text-sm text-gray-600">
                            <Phone className="h-4 w-4 mr-2 text-orange-500" />
                            <a href={`tel:${submission.phone}`} className="hover:text-orange-600">
                                {submission.phone}
                            </a>
                        </div>
                    )}

                    {formatted.hasLocation && (
                        <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mr-2 text-orange-500" />
                            {submission.location}
                        </div>
                    )}

                    <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {formatted.formattedDate}
                        <Clock className="h-4 w-4 ml-3 mr-2 text-gray-400" />
                        {formatted.formattedTime}
                    </div>
                </div>

                {/* Contact Reason */}
                {formatted.hasContactReason && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <p className="text-xs text-gray-500 mb-1 flex items-center">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Razón de Contacto
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-3">{submission.contact_reason}</p>
                    </div>
                )}

                {/* Resolution Info (if resolved) */}
                {formatted.resolved && formatted.hasResolution && (
                    <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-100">
                        <p className="text-xs text-green-600 mb-1 flex items-center">
                            <FileText className="h-3 w-3 mr-1" />
                            Resolución
                        </p>
                        <p className="text-sm text-green-800 line-clamp-3">{submission.resolution}</p>
                        {submission.admin_id && (
                            <p className="text-xs text-green-600 mt-2">
                                Resuelto por: {submission.admin_id}
                            </p>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100 mt-auto">
                    {/* Delete Button - Left Side */}
                    <button
                        onClick={() => openDeleteModal(submission)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar solicitud"
                    >
                        <Trash2 className="h-5 w-5" />
                    </button>

                    {/* Resolve/Unresolve Actions - Right Side */}
                    <div className="flex space-x-2">
                        {formatted.resolved ? (
                            <button
                                onClick={() => handleUnresolve(submission)}
                                className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
                            >
                                Reabrir
                            </button>
                        ) : (
                            <button
                                onClick={() => openResolveModal(submission)}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                            >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Resolver
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">
            {/* Header */}
            <div className="bg-white border-b border-orange-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                                <MessageSquare className="h-7 w-7 text-orange-500 mr-3" />
                                Solicitudes de Contacto
                            </h1>
                            <p className="text-gray-500 mt-1">
                                Gestiona las solicitudes de contacto recibidas
                            </p>
                        </div>

                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Actualizar
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-white rounded-xl shadow-md border border-orange-200 p-4">
                            <p className="text-xs text-orange-600 uppercase tracking-wide">Pendientes</p>
                            <p className="text-2xl font-bold text-orange-600">{stats.unresolved_count}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-md border border-orange-200 p-4">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
                            <p className="text-2xl font-bold text-gray-800">{stats.total_submissions}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-md border border-green-200 p-4">
                            <p className="text-xs text-green-600 uppercase tracking-wide">Resueltas</p>
                            <p className="text-2xl font-bold text-green-600">{stats.resolved_count}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-md border border-blue-200 p-4">
                            <p className="text-xs text-blue-600 uppercase tracking-wide">Hoy</p>
                            <p className="text-2xl font-bold text-blue-600">{stats.submissions_today}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-md border border-purple-200 p-4">
                            <p className="text-xs text-purple-600 uppercase tracking-wide">Este Mes</p>
                            <p className="text-2xl font-bold text-purple-600">{stats.submissions_this_month}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filters */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                <div className="bg-white rounded-xl shadow-md border border-orange-200 p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Search Input */}
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Buscar por nombre, email o teléfono..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                            {searchTerm && (
                                <button
                                    onClick={handleClearSearch}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                </button>
                            )}
                        </div>

                        {/* Search Button */}
                        <button
                            onClick={handleSearch}
                            disabled={isSearching || loading}
                            className="px-6 py-2 bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 transition-all duration-200"
                        >
                            {isSearching ? (
                                <div className="flex items-center">
                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                    Buscando...
                                </div>
                            ) : (
                                <div className="flex items-center">
                                    <Search className="h-4 w-4 mr-2" />
                                    Buscar
                                </div>
                            )}
                        </button>

                        {/* Filter Dropdown */}
                        <div className="relative">
                            <select
                                value={filterStatus}
                                onChange={(e) => {
                                    setFilterStatus(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="appearance-none pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white cursor-pointer"
                            >
                                <option value="unresolved">Pendientes</option>
                                <option value="all">Todas</option>
                                <option value="resolved">Resueltas</option>
                            </select>
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
                            <span className="text-red-700">{error}</span>
                        </div>
                        <button onClick={clearError} className="text-red-500 hover:text-red-700">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
                {isLoadingInitial ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Loader2 className="h-12 w-12 text-orange-500 animate-spin mb-4" />
                        <p className="text-gray-500">Cargando solicitudes...</p>
                    </div>
                ) : submissions.length > 0 ? (
                    <>
                        {/* Results Info */}
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm text-gray-500">
                                Mostrando {submissions.length} de {totalCount} solicitudes
                            </p>
                        </div>

                        {/* Submissions Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {submissions.map(renderSubmissionCard)}
                        </div>

                        {/* Pagination */}
                        {totalCount > itemsPerPage && (
                            <div className="flex items-center justify-center space-x-4 mt-8">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1 || loading}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Anterior
                                </button>
                                <span className="text-sm text-gray-600">
                                    Página {currentPage} de {Math.ceil(totalCount / itemsPerPage)}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                    disabled={!hasMore || loading}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Siguiente
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-16">
                        <MessageSquare className="h-16 w-16 text-orange-300 mx-auto mb-4" />
                        <h3 className="text-xl font-medium text-gray-700 mb-2">
                            No hay solicitudes de contacto
                        </h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                            {searchTerm
                                ? `No se encontraron resultados para "${searchTerm}"`
                                : filterStatus !== 'all'
                                    ? `No hay solicitudes ${filterStatus === 'resolved' ? 'resueltas' : 'pendientes'}`
                                    : 'Las solicitudes de contacto aparecerán aquí cuando sean recibidas.'
                            }
                        </p>
                    </div>
                )}
            </main>

            {/* Resolve Modal */}
            {showResolveModal && selectedSubmission && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                                    <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                                    Resolver Solicitud
                                </h2>
                                <button
                                    onClick={() => setShowResolveModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            {/* Submission Summary */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <h3 className="font-medium text-gray-800 mb-2">{selectedSubmission.name}</h3>
                                <p className="text-sm text-gray-600">{selectedSubmission.email}</p>
                                {selectedSubmission.contact_reason && (
                                    <p className="text-sm text-gray-500 mt-2 italic">
                                        "{selectedSubmission.contact_reason}"
                                    </p>
                                )}
                            </div>

                            {/* Resolution Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notas de Resolución (opcional)
                                </label>
                                <textarea
                                    value={resolutionNotes}
                                    onChange={(e) => setResolutionNotes(e.target.value)}
                                    placeholder="Describe cómo se resolvió esta solicitud..."
                                    rows={4}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowResolveModal(false)}
                                disabled={isResolving}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleResolve}
                                disabled={isResolving}
                                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                            >
                                {isResolving ? (
                                    <>
                                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                        Resolviendo...
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4 mr-2" />
                                        Marcar como Resuelto
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal (NEW) */}
            {showDeleteModal && submissionToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        <div className="p-6">
                            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto mb-4">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
                                ¿Eliminar solicitud?
                            </h3>
                            <p className="text-sm text-gray-500 text-center mb-6">
                                ¿Estás seguro de que deseas eliminar la solicitud de <span className="font-semibold">{submissionToDelete.name}</span>? Esta acción no se puede deshacer.
                            </p>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex justify-center items-center"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="animate-spin h-4 w-4" />
                                    ) : (
                                        'Eliminar'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Wrapper component with Provider
const ContactSubmissions = () => {
    return (
        <ContactSubmissionsProvider>
            <ContactSubmissionsInner />
        </ContactSubmissionsProvider>
    );
};

export default ContactSubmissions;